import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Flex, Input, Tooltip } from "@fluentui/react-northstar";
import { RetryIcon, SearchIcon } from "@fluentui/react-icons-northstar";
import filter from "lodash/filter";
import Alert from "../components/common/alert";
import ProjectList from "../components/projectList";
import IProject from "../interfaces/IProject";

import { useApp } from "../contexts/appContext";
import { useProjectsWithDetails } from "../queries";
import { getErrorMessage, isAuthIssue } from "../queries/queryClient";
import styles from "../common.module.scss";
import { getSelectedProjectView, setSelectedProjectView } from "../services/settingsService";


const viewNameToViewId = (value: string): number => {
    switch (value) {
        case "Otevřené": return 0;
        case "Aktivní": return 1;
        case "Pozastavené": return 2;
        case "K revizi": return 3;
        case "ServiceDesk": return 4;
        default: return 99;
    }
};

const views = ["Otevřené", "Aktivní", "Pozastavené", "K revizi", "ServiceDesk", "Uzavřené"];

const Projects: React.FunctionComponent<{}> = () => {
    document.title = "Projects";
    const { state: appState, dispatch } = useApp();
    const { backgroundColor } = appState;

    const [query, setQuery] = useState(appState.projectsQuery);
    const [selectedView, setSelectedView] = useState(getSelectedProjectView());

    const { data: projectsData, error, refetch } = useProjectsWithDetails(viewNameToViewId(selectedView));
    const projects: IProject[] = useMemo(() => projectsData ?? [], [projectsData]);

    useEffect(() => {
        dispatch({ type: "SET_PROJECTS_QUERY", query });
    }, [query]);

    const filteredProjects = useMemo(() => {
        if (!query) { return projects; }

        const lower = query.toLowerCase();
        const words = lower.split(" ");

        return filter(projects, (x: IProject) => {
            const subject = x.subject.toLowerCase();

            // tslint:disable-next-line: no-shadowed-variable
            const match: boolean[] = words.map((_: any) => false);
            for (let index = 0; index < words.length; index++) {
                match[index] = subject.indexOf(words[index]) > -1;
            }

            return match.filter((m: boolean) => m).length === match.length;
        });
    }, [query, projects]);

    const viewChanged = async (_: any, data: any) => {
        const value = data.value;
        setSelectedView(value);
        setSelectedProjectView(value);
    };

    return (
        <div>
            <div className={styles.centerContainer} style={{ minHeight: 100 }}>
                <Flex className={styles.stickySearch} style={{ backgroundColor }}>
                    <Dropdown
                        defaultValue={selectedView}
                        items={views}
                        onChange={viewChanged}
                        style={{ padding: "1em 0 1em 1em", width: "180px" }}
                        fluid
                    />
                    <Input
                        value={query} placeholder="Vyhledávání" onChange={(_: any, data: any) => setQuery(data.value)}
                        icon={<SearchIcon />} style={{ padding: "1em" }}
                        clearable fluid
                    />
                </Flex>
                {error && <Alert type="error" authIssue={isAuthIssue(error)}>{getErrorMessage(error)}</Alert>}
                <ProjectList items={filteredProjects} />
            </div>

            <div className={styles.footer} style={{ background: backgroundColor }}>
                <Flex gap="gap.small" className={styles.centerContainer}>
                    <Tooltip
                        trigger={<Button text iconOnly icon={<RetryIcon />} onClick={() => refetch()} />}
                        content="Aktualizovat seznam případů"
                    />
                </Flex>
            </div>
        </div>
    );
};

export default Projects;
